/** Base class for domain errors that map to HTTP status codes. */
export abstract class ApiError extends Error {
  abstract override readonly name: string
  abstract readonly statusCode: number
}

/**
 * The over-the-wire shape of a serialised `ApiError` subclass — all class
 * fields minus internal-only properties (`statusCode`, `stack`, `cause`).
 *
 * Wrapped in a conditional to make the type distributive over unions, so
 * `SerializedError<ErrorA | ErrorB>` evaluates each member independently.
 *
 * @example
 * ```ts
 * export type PostDispensationCheckoutError = SerializedError<InvalidCardError | ConflictError>
 * // = { name: 'InvalidCardError'; message: string; customerPortalUrl: string } | { name: 'ConflictError'; message: string }
 * ```
 */
export type SerializedError<T extends ApiError> = T extends ApiError
  ? Omit<T, 'statusCode' | 'stack' | 'cause'> & { message: string }
  : never

export class UnauthorizedError extends ApiError {
  override readonly name = 'UnauthorizedError'
  readonly statusCode = 401
}

export class ForbiddenError extends ApiError {
  override readonly name = 'ForbiddenError'
  readonly statusCode = 403
}

export class NotFoundError extends ApiError {
  override readonly name = 'NotFoundError'
  readonly statusCode = 404
}

export class ValidationError extends ApiError {
  override readonly name = 'ValidationError'
  readonly statusCode = 400
  readonly details?: unknown[]

  constructor(message?: string, details?: unknown[]) {
    super(message)
    this.details = details
  }
}

export class ConflictError extends ApiError {
  override readonly name = 'ConflictError'
  readonly statusCode = 409
}

/** Errors any route may answer with, so route definitions never declare them. */
export const GENERIC_API_ERRORS = [
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
  ConflictError,
] as const

/** The body of a generic error, or of a failure the error middleware reports without an `ApiError`. */
export type GenericApiError =
  | SerializedError<InstanceType<(typeof GENERIC_API_ERRORS)[number]>>
  | { name: 'HTTPError' | 'InternalError'; message: string }
